import { useState, useEffect } from "react";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Textarea } from "@/components/ui/Textarea";
import { Label } from "@/components/ui/Label";
import CryptoJS from "crypto-js";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { Copy, RotateCcw, ShieldCheck } from "lucide-react";
import { copyToClipboard } from "@/lib/utils";

const DEFAULT_HEADER = {
  alg: "HS256",
  typ: "JWT"
};

const DEFAULT_PAYLOAD = {
  sub: "1234567890",
  name: "John Doe",
  admin: true,
  iat: Math.floor(Date.now() / 1000),
  exp: Math.floor(Date.now() / 1000) + 3600
};

export function JwtGenerator() {
  const [header, setHeader] = useState(JSON.stringify(DEFAULT_HEADER, null, 2));
  const [payload, setPayload] = useState(JSON.stringify(DEFAULT_PAYLOAD, null, 2));
  const [secret, setSecret] = useState("your-256-bit-secret");
  const [algo, setAlgo] = useState("HS256");
  const [token, setToken] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  // Helper function to encode WordArray to Base64URL
  const base64url = (source: CryptoJS.lib.WordArray): string => {
    let encoded = CryptoJS.enc.Base64.stringify(source);
    return encoded.replace(/=+$/, "").replace(/\+/g, "-").replace(/\//g, "_");
  };

  // Helper to convert UTF-8 string to base64url
  const encodeString = (str: string): string => {
    const wa = CryptoJS.enc.Utf8.parse(str);
    return base64url(wa);
  };

  const generateJwt = () => {
    try {
      setError("");
      
      // Parse header and payload to validate JSON
      const parsedHeader = JSON.parse(header);
      const parsedPayload = JSON.parse(payload);

      // Force header alg to match chosen UI algorithm
      parsedHeader.alg = algo;
      
      const headerStr = JSON.stringify(parsedHeader);
      const payloadStr = JSON.stringify(parsedPayload);

      const headerB64 = encodeString(headerStr);
      const payloadB64 = encodeString(payloadStr);

      const headerAndPayload = `${headerB64}.${payloadB64}`;

      let signatureWA;
      switch (algo) {
        case "HS384":
          signatureWA = CryptoJS.HmacSHA384(headerAndPayload, secret);
          break;
        case "HS512":
          signatureWA = CryptoJS.HmacSHA512(headerAndPayload, secret);
          break;
        case "HS256":
        default:
          signatureWA = CryptoJS.HmacSHA256(headerAndPayload, secret);
          break;
      }

      const signatureB64 = base64url(signatureWA);
      setToken(`${headerAndPayload}.${signatureB64}`);
    } catch (e: any) {
      setError(`Invalid JSON structure: ${e.message}`);
      setToken("");
    }
  };

  useEffect(() => {
    generateJwt();
  }, [header, payload, secret, algo]);

  const handleCopy = () => {
    if (!token) return;
    copyToClipboard(token).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const setExpiryIn = (seconds: number) => {
    try {
      const current = JSON.parse(payload);
      current.iat = Math.floor(Date.now() / 1000);
      current.exp = Math.floor(Date.now() / 1000) + seconds;
      setPayload(JSON.stringify(current, null, 2));
    } catch (e) {
      // If current payload is broken JSON, fallback to default structure
      const fresh = { ...DEFAULT_PAYLOAD, iat: Math.floor(Date.now() / 1000), exp: Math.floor(Date.now() / 1000) + seconds };
      setPayload(JSON.stringify(fresh, null, 2));
    }
  };

  const handleReset = () => {
    setHeader(JSON.stringify(DEFAULT_HEADER, null, 2));
    setPayload(JSON.stringify({
      ...DEFAULT_PAYLOAD,
      iat: Math.floor(Date.now() / 1000),
      exp: Math.floor(Date.now() / 1000) + 3600
    }, null, 2));
    setSecret("your-256-bit-secret");
    setAlgo("HS256");
  };

  return (
    <ToolPageLayout
      title="JWT Generator Online - Create JSON Web Tokens"
      description="Create, sign, and customize JSON Web Tokens (JWT) using secure client-side HMAC signatures. Fully customize headers, payloads, algorithms, and exp/iat claims."
      h1="JWT Generator"
      category="JWT & Security"
      categoryPath="/jwt-security"
      howToUse={[
        "Choose a signing algorithm (HS256, HS384, or HS512).",
        "Customize your claims in the JSON Payload editor (you can use quick helper buttons to set expiry timestamps).",
        "Enter a signature secret key or keep the default key.",
        "Your signed JSON Web Token is generated instantly! Copy it to your clipboard."
      ]}
      faq={[
        { question: "What is an online JWT generator and how does it work?", answer: "An online JWT generator creates cryptographically signed JSON Web Tokens. It takes a JSON Header, a JSON Payload (containing user roles and authorization claims), and signs them using a selected hash algorithm (like HMAC-SHA256) combined with a secret passphrase or key." },
        { question: "Are my secrets transmitted to a server during online JWT generation?", answer: "No, absolutely not. This JWT generator calculates cryptographic signatures 100% locally inside your web browser using CryptoJS. Your shared secrets are never sent over the network, making this safe for developer environments." },
        { question: "What is the difference between HS256, HS384, and HS512?", answer: "These are symmetric HMAC signing algorithms. The numbers represent the output bit-length of the underlying SHA hash functions: HS256 utilizes SHA-256, HS384 utilizes SHA-384, and HS512 utilizes SHA-512. Higher numbers offer superior collision resistance and strength but produce slightly longer token strings." },
        { question: "How do I define or set an expiration timestamp (exp claim) in a JWT?", answer: "The 'exp' claim represents the expiration time on or after which the JWT must not be accepted for processing. It must be a numeric value representing seconds elapsed since Epoch (Unix timestamp). You can use our quick +1 hour or +1 day buttons to generate this automatically." },
        { question: "Why does the JWT generator output dots ('.') in the generated token?", answer: "JSON Web Tokens are structurally formatted using 'JWS Compact Serialization'. This compact representation separates the Base64URL-encoded Header, Payload, and Signature using exact single dots to facilitate fast, efficient parsing in web routers and API gateways." },
        { question: "What is the standard purpose of the 'iat' and 'nbf' claims in a token payload?", answer: "'iat' stands for 'Issued At' (when the token was created), and 'nbf' stands for 'Not Before' (the exact timestamp before which the token must be rejected as inactive). Both assist servers in assessing validity." },
        { question: "How does symmetric signing differ from asymmetric signing in JWTs?", answer: "Symmetric signing (e.g., HS256) uses the same secret key to generate and verify signatures. Asymmetric signing (e.g., RS256) uses a private key to sign the token and a corresponding public key to verify it, which is ideal for multi-service environments where verification keys are public." },
        { question: "Can a client-side browser read the contents of a signed JWT?", answer: "Yes, standard JWS-signed tokens are only signed, not encrypted. This means the payload is readable by anyone who decodes the Base64URL string. Never store sensitive, unencrypted client secrets, personal data, or passwords inside standard JWT claims." },
        { question: "What makes a secret key secure for signing HS256 tokens?", answer: "A secure HS256 secret key should be high-entropy, random, and at least 256 bits (32 bytes) long. Short, dictionary-based secret keys are highly vulnerable to brute-force offline cracking attacks." },
        { question: "What is the difference between a JWT signed with HMAC versus a JWT encrypted with JWE?", answer: "An HMAC-signed JWT guarantees data integrity and authenticity (no one can modify the data without invalidating the signature), while a JWE (JSON Web Encryption) token guarantees confidentiality (the payload is fully encrypted, meaning unauthorized parties cannot read its contents)." }
      ]}
    >
      <div className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Inputs Section */}
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-semibold text-slate-700">Algorithm</Label>
              <select
                value={algo}
                onChange={(e) => setAlgo(e.target.value)}
                className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm mt-1 focus:ring-2 focus:ring-rose-500 outline-none"
              >
                <option value="HS256">HS256 (HMAC-SHA256)</option>
                <option value="HS384">HS384 (HMAC-SHA384)</option>
                <option value="HS512">HS512 (HMAC-SHA512)</option>
              </select>
            </div>

            <div>
              <Label className="text-sm font-semibold text-slate-700">Secret Key</Label>
              <Input
                type="text"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                className="mt-1 font-mono text-sm"
                placeholder="Enter secret key..."
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <Label className="text-sm font-semibold text-slate-700">Header (JSON)</Label>
                <span className="text-xs text-slate-400">Read-Only alg parameter</span>
              </div>
              <Textarea
                value={header}
                onChange={(e) => setHeader(e.target.value)}
                className="font-mono text-xs min-h-[80px]"
                placeholder="JWT Header..."
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <Label className="text-sm font-semibold text-slate-700">Payload (JSON)</Label>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => setExpiryIn(3600)}
                    className="text-[10px] bg-slate-100 hover:bg-slate-200 text-slate-600 px-2 py-0.5 rounded-md font-medium transition-colors"
                  >
                    +1 Hr Exp
                  </button>
                  <button
                    onClick={() => setExpiryIn(86400)}
                    className="text-[10px] bg-slate-100 hover:bg-slate-200 text-slate-600 px-2 py-0.5 rounded-md font-medium transition-colors"
                  >
                    +1 Day Exp
                  </button>
                </div>
              </div>
              <Textarea
                value={payload}
                onChange={(e) => setPayload(e.target.value)}
                className="font-mono text-xs min-h-[160px]"
                placeholder="JWT Payload..."
              />
            </div>
          </div>

          {/* Results Section */}
          <div className="flex flex-col justify-between bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4">
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-rose-600">
                <ShieldCheck className="w-5 h-5 shrink-0" />
                <span className="font-semibold text-sm uppercase tracking-wider">Signed Token Output</span>
              </div>

              {error ? (
                <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-xs font-mono">
                  {error}
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="p-4 bg-white border border-slate-200 rounded-xl font-mono text-xs select-all break-all leading-relaxed text-slate-800">
                    {token}
                  </div>
                  <div className="text-xs text-slate-500 leading-relaxed space-y-1">
                    <p>🟢 <span className="text-red-500 font-bold">Header</span> (Base64URL encoded)</p>
                    <p>🟢 <span className="text-blue-500 font-bold">Payload</span> (Base64URL encoded)</p>
                    <p>🟢 <span className="text-emerald-500 font-bold">Signature</span> (Verification Key)</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleCopy}
                disabled={!!error || !token}
                className="flex-1 bg-slate-900 hover:bg-slate-800 text-white gap-2"
              >
                <Copy className="w-4 h-4" />
                {copied ? "Copied!" : "Copy Signed JWT"}
              </Button>
              <Button
                variant="outline"
                onClick={handleReset}
                className="gap-2"
                title="Reset to default settings"
              >
                <RotateCcw className="w-4 h-4" />
                Reset
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}
